/**
 * @file PhotoTypeFields Organism Component
 * @description Task 생성/수정 페이지에서 사용되는 Photo Type 필드 섹션
 * - 동적 photoTypeItems 목록 렌더링
 * - 고정 항목: Close-up Seal (필수), Extra (선택)
 * - add/edit 페이지 간 코드 중복 제거
 */

import React, { ReactNode } from "react";
import { Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { removeEmoji } from "@/src/utils/string";
import Flex from "@/src/components/molecule/Flex";
import Loader from "@/src/components/atom/Loader";
import Typo from "@/src/components/atom/Typo";
import Input from "@/src/components/atom/Input";
import CheckboxCountField from "@/src/components/molecule/CheckboxCountField";
import colorSet from "@/src/styles/color";
import typo from "@/src/styles/typography";
import type { PhotoTypeFormItem } from "@/src/pages/exporter/Task/types";

export interface PhotoTypeFieldsProps {
  photoTypeItems: PhotoTypeFormItem[];
  closeUpSeal: { count: number; isUse: boolean } | undefined;
  extra: { count: number; isUse: boolean; remark?: string } | undefined;
  isLoading: boolean;
  onUpdatePhotoTypeItem: (index: number, updates: Partial<PhotoTypeFormItem>) => void;
  onUpdateCloseUpSeal: (updates: Partial<{ count: number; isUse: boolean }>) => void;
  onUpdateExtra: (updates: Partial<{ count: number; isUse: boolean; remark?: string }>) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  headerContent?: ReactNode;
  checkboxDisabled?: boolean;
}

/**
 * PhotoTypeFields - Photo Type 카운터 필드 섹션
 *
 * @description
 * Task 생성/수정 페이지의 Photo Type 선택 영역을 담당합니다.
 * - 동적 photo type 목록 (photoTypeConfig에서 로드)
 * - Close-up Seal: 항상 필수, 체크박스 비활성화
 * - Extra: 선택적, remark 입력 필드 포함
 *
 * @example
 * ```tsx
 * <PhotoTypeFields
 *   photoTypeItems={photoTypeItems}
 *   closeUpSeal={watchedCloseUpSeal}
 *   extra={watchedExtra}
 *   isLoading={isPhotoTypeConfigLoading}
 *   onUpdatePhotoTypeItem={updatePhotoTypeItem}
 *   onUpdateCloseUpSeal={updateCloseUpSeal}
 *   onUpdateExtra={updateExtra}
 *   control={control}
 *   headerContent={<EditButton />}
 * />
 * ```
 */
const PhotoTypeFields: React.FC<PhotoTypeFieldsProps> = ({
  photoTypeItems,
  closeUpSeal,
  extra,
  isLoading,
  onUpdatePhotoTypeItem,
  onUpdateCloseUpSeal,
  onUpdateExtra,
  control,
  headerContent,
  checkboxDisabled = false,
}) => {
  const { t } = useTranslation();

  return (
    <RequestPhotoTypeContainer>
      <RequestPhotoTypeHeader>
        <Typo typoType="b7m" color="gray2">
          {t("task:add.common.requestPhotoType")}
        </Typo>
        {headerContent}
      </RequestPhotoTypeHeader>

      <PhotoTypeCounterContent>
        {isLoading ? (
          <Flex
            justifyContent="center"
            alignItems="center"
            style={{ padding: "40px" }}
          >
            <Loader />
          </Flex>
        ) : (
          <>
            {photoTypeItems.length === 0 && (
              <Flex
                justifyContent="center"
                alignItems="center"
                style={{ padding: "40px 40px 0" }}
              >
                <Typo typoType="b7r" color="gray6">
                  {t("task:add.info.noPhotoTypeConfig")}
                </Typo>
              </Flex>
            )}

            {photoTypeItems
              .map((item, originalIndex) => ({ item, originalIndex }))
              .filter(
                ({ item }) =>
                  item.typeCode !== "CLOSE_UP_SEAL" && item.typeCode !== "EXTRA"
              )
              .map(({ item, originalIndex }) => {
                const cleanTitle = removeEmoji(item.title);
                const cleanSubtitle = item.subTitle
                  ? removeEmoji(item.subTitle)
                  : null;

                return (
                  <StyledCheckboxCountField
                    key={`${item.typeCode}-${originalIndex}`}
                    label={
                      <PhotoTypeLabelContainer>
                        <span>{cleanTitle}</span>
                        {cleanSubtitle && (
                          <PhotoTypeSubtitle>{cleanSubtitle}</PhotoTypeSubtitle>
                        )}
                      </PhotoTypeLabelContainer>
                    }
                    isChecked={item.isUse}
                    count={item.count}
                    min={item.minCount ?? 1}
                    checkboxDisabled={checkboxDisabled}
                    onCountDown={() =>
                      onUpdatePhotoTypeItem(originalIndex, {
                        count: item.count - 1,
                      })
                    }
                    onCountUp={() =>
                      onUpdatePhotoTypeItem(originalIndex, {
                        count: item.count + 1,
                      })
                    }
                    onCheckChange={() =>
                      onUpdatePhotoTypeItem(originalIndex, {
                        isUse: !item.isUse,
                      })
                    }
                  />
                );
              })}

            <StyledCheckboxCountField
              key="close-up-seal"
              label={
                <PhotoTypeLabelContainer>
                  <span>
                    Close Up Seal <strong>*</strong>
                  </span>
                </PhotoTypeLabelContainer>
              }
              isChecked={true}
              count={closeUpSeal?.count || 1}
              min={1}
              checkboxDisabled={checkboxDisabled}
              onCountDown={() =>
                onUpdateCloseUpSeal({ count: (closeUpSeal?.count || 1) - 1 })
              }
              onCountUp={() =>
                onUpdateCloseUpSeal({ count: (closeUpSeal?.count || 1) + 1 })
              }
              forceChecked
            />

            <StyledCheckboxCountField
              key="extra"
              label={
                <PhotoTypeLabelContainer>
                  <span>Extra</span>
                </PhotoTypeLabelContainer>
              }
              isChecked={extra?.isUse || false}
              count={extra?.count || 1}
              checkboxDisabled={checkboxDisabled}
              onCountDown={() =>
                onUpdateExtra({ count: (extra?.count || 1) - 1 })
              }
              onCountUp={() =>
                onUpdateExtra({ count: (extra?.count || 1) + 1 })
              }
              onCheckChange={() => onUpdateExtra({ isUse: !extra?.isUse })}
              bottomAccessory={
                <Controller
                  control={control}
                  name="extra.remark"
                  render={({ field }) => (
                    <Input
                      {...field}
                      disabled={!extra?.isUse}
                      onClear={() => onUpdateExtra({ remark: "" })}
                      placeholder={t("task:add.placeholder.photoTypeRemark")}
                    />
                  )}
                />
              }
            />
          </>
        )}
      </PhotoTypeCounterContent>
    </RequestPhotoTypeContainer>
  );
};

export default PhotoTypeFields;

// Styled Components
const RequestPhotoTypeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const RequestPhotoTypeHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const PhotoTypeCounterContent = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  row-gap: 12px;
  column-gap: 24px;

  strong {
    color: ${colorSet.red2};
  }
`;

const StyledCheckboxCountField = styled(CheckboxCountField)`
  overflow: auto;
  align-self: stretch;

  > div:first-child > div:first-child {
    input[type="checkbox"] {
      margin-top: 3px;
    }
  }
`;

const PhotoTypeLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: hidden;
  min-width: 0;
`;

const PhotoTypeSubtitle = styled.span`
  ${typo.b9r};
  color: ${colorSet.gray5};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
`;
